import React from "react";
import { useNavigate } from "react-router-dom";
import { StyledButton } from "@aureskonnect/react-ui";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
    setMultiShopSwitch,
    setNumberClick,
    setOneShopSwitch,
} from "@components/VerticalLayout/store";

import { getData } from "../../helpers/general";

import "./index.css";
import {
    setAllAuthorized,
    setEntityDetails,
    setFranchiseCountries,
    setIsFoodCourt,
    setPaymentType,
    setSelectedAccount,
    setSelectedEntity,
} from "../../redux/globalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useFetchGlobal } from "../../api/hooks";
import type { RootState } from "../../redux/store";
import {
    collectCountries,
    findNodeByUid,
    getCommercialName,
    uniqueNonEmpty,
} from "./treeHelpers";
import type { CustomIconsProps, SelectedRow } from "./types";

const BOUTIQUE_VALUES: ReadonlySet<string> = new Set(["boutique"]);

function matchesSet(value: unknown, set: ReadonlySet<string>): boolean {
    return typeof value === "string" && set.has(value.toLowerCase());
}

function isBoutiqueLike(row: SelectedRow): boolean {
    return (
        matchesSet(row["Account type"], BOUTIQUE_VALUES) ||
        matchesSet(row.Nature, BOUTIQUE_VALUES) ||
        matchesSet(row.Kind, BOUTIQUE_VALUES)
    );
}

function getCountry(row: SelectedRow, translatedKey: string): string {
    const translated = row[translatedKey];
    if (typeof translated === "string" && translated) return translated;
    return typeof row.Country === "string" ? row.Country : "";
}

export function CustomIconsComponent({ e }: CustomIconsProps) {
    const { fetchGlobal } = useFetchGlobal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mainColor = useSelector(
        (state: RootState) => state.global.infoUser.maincolor
    );
    const entityTree = useSelector(
        (state: RootState) => state.global.entityDetails
    );
    const uuidUser = localStorage.getItem("uuidUser");

    function setShop(
        franchiseId: string,
        shopId: string,
        pays: string,
        shopName: string
    ): void {
        setOneShopSwitch(franchiseId, shopId, pays, shopName);
        navigate("/setting/customer-account");
    }

    function setFranchise(franchiseId: string, shopName: string): void {
        setMultiShopSwitch(franchiseId, shopName);
        navigate("/setting/customer-account");
    }

    async function handleOnClickEvent(): Promise<void> {
        setNumberClick(0);

        const row = e.selectedRow;
        const isFranchiseRow = row.Kind === "Franchise";
        const franchiseId =
            (isFranchiseRow ? row.uid : row.uuidFranchise) ?? "";
        const shopId = isFranchiseRow ? "0" : row.uid ?? "";

        if (!franchiseId || !row.uid) {
            toast.error(`${t("There's an error")}!`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        if (uuidUser) {
            await getData(franchiseId, uuidUser, shopId, fetchGlobal);
        }

        dispatch(setSelectedEntity(row));

        const franchiseNode = findNodeByUid(entityTree, franchiseId);
        if (franchiseNode) {
            dispatch(setEntityDetails(franchiseNode));
            dispatch(
                setFranchiseCountries(
                    uniqueNonEmpty(collectCountries(franchiseNode))
                )
            );
        }

        const commercialName = getCommercialName(row, t("Commercial name"));
        const country = getCountry(row, t("Country"));
        const paymentType = row.paymentType ?? 0;

        if (isBoutiqueLike(row)) {
            dispatch(setIsFoodCourt(row.isFoodCourt === true));
            dispatch(setPaymentType(paymentType));
            dispatch(setSelectedAccount(commercialName));

            setShop(franchiseId, row.uid, country, commercialName);
        } else {
            dispatch(setIsFoodCourt(false));
            dispatch(setPaymentType(paymentType));

            const activeChildren =
                row.childs_profilelink?.filter((el) => el.etat === "Activé") ??
                [];
            const subRowCount = row.subRows?.length ?? 0;
            dispatch(setAllAuthorized(activeChildren.length === subRowCount));

            dispatch(setSelectedAccount(commercialName));
            setFranchise(row.uid, commercialName);
        }
    }

    const accountTypeKey = t("Account type");
    const isAccessibleRow = [
        e.selectedRow.Nature,
        e.selectedRow[accountTypeKey],
        e.selectedRow.Kind,
    ].some(
        (v) =>
            typeof v === "string" &&
            ["franchise", "boutique"].includes(v.toLowerCase())
    );

    return (
        <React.Fragment>
            {isAccessibleRow ? (
                <StyledButton
                    outline
                    rounded
                    variant={mainColor}
                    onClick={handleOnClickEvent}
                >
                    {t("To access")}
                </StyledButton>
            ) : (
                <div></div>
            )}
        </React.Fragment>
    );
}
